import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Alert, Button, Card, Spinner } from "react-bootstrap";
import { api } from "../../helpers/apiCore";
import { endpoints } from "../../helpers/Endpoints";
import FormInput from "../../components/FormInput";
import "../../styles/auth.css";

interface AlertState {
  show: boolean;
  message: string;
  type: 'success' | 'danger';
}

interface LoadingState {
  verifying: boolean;
  resending: boolean;
}

const INITIAL_ALERT: AlertState = { show: false, message: '', type: 'success' };
const INITIAL_LOADING: LoadingState = { verifying: false, resending: false };
const REDIRECT_DELAY = 3000;
const ALERT_TIMEOUT = 5000;
const OTP_MIN_LENGTH = 4;
const OTP_MAX_LENGTH = 6;

const EmailVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [alert, setAlert] = useState<AlertState>(INITIAL_ALERT);
  const [loading, setLoading] = useState<LoadingState>(INITIAL_LOADING);

  // Utility functions
  const showAlert = useCallback((message: string, type: AlertState['type'] = 'danger') => {
    setAlert({ show: true, message, type });
  }, []);

  const hideAlert = useCallback(() => {
    setAlert(INITIAL_ALERT);
  }, []);

  const redirectToDashboard = useCallback(() => {
    navigate('/admin/dashboard');
  }, [navigate]);

  const isFormValid = useCallback(() => {
    return email && otp && otp.trim().length >= OTP_MIN_LENGTH;
  }, [email, otp]);

  const makeApiCall = useCallback(async (
    endpoint: string, 
    data: any, 
    loadingKey: keyof LoadingState,
    successMessage: string
  ) => {
    setLoading(prev => ({ ...prev, [loadingKey]: true }));
    hideAlert();

    try {
      api.setAuthorization(api.getLoggedInUser());
      await api.create(endpoint, data);
      showAlert(successMessage, 'success');
      return true;
    } catch (error: any) {
      const message = error?.response?.data?.message || `Operation failed. Please try again.`;
      showAlert(message);
      return false;
    } finally {
      setLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  }, [showAlert, hideAlert]);

  const handleVerifyEmail = useCallback(async () => {
    if (!email) {
      showAlert("Email is required for verification");
      return;
    }
    if (!otp || otp.trim().length === 0) {
      showAlert("OTP is required for verification");
      return;
    }

    const success = await makeApiCall(
      endpoints.verifyEmail,
      { email, otp: otp.trim() },
      'verifying',
      "Email verified successfully! Redirecting to dashboard..."
    );

    if (success) {
      setIsVerified(true);
      setTimeout(redirectToDashboard, REDIRECT_DELAY);
    }
  }, [email, otp, makeApiCall, showAlert, redirectToDashboard]);

  const handleResendVerification = useCallback(async () => {
    const success = await makeApiCall(
      endpoints.verifyEmail,
      { email },
      'resending',
      "Verification OTP sent successfully! Please check your email."
    );

    if (success) {
      setOtp("");
    }
  }, [email, makeApiCall]);

  const handleOtpChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d{0,6}$/.test(value)) {
      setOtp(value);
      if (alert.show && alert.type === 'danger') {
        hideAlert();
      }
    }
  }, [alert.show, alert.type, hideAlert]);

  // Effects
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    } else {
      showAlert("No email provided in URL");
    }
  }, [searchParams, showAlert]);

  useEffect(() => {
    if (!alert.show || isVerified) return;

    const timer = setTimeout(() => {
      hideAlert();
    }, ALERT_TIMEOUT);

    return () => clearTimeout(timer);
  }, [alert.show, isVerified, hideAlert]);

  const renderLoadingButton = (isLoading: boolean, loadingText: string, defaultText: string) => (
    <>
      {isLoading && <Spinner animation="border" size="sm" className="me-2" />}
      {isLoading ? loadingText : defaultText}
    </>
  );

  const renderVerificationForm = () => (
    <div className="verification-form">
      <form className="card-form-row">
        <FormInput
          type="text"
          label="Enter OTP"
          name="otp"
          className="custom-input"
          value={otp}
          onChange={handleOtpChange}
          placeholder={`Enter ${OTP_MAX_LENGTH}-digit OTP`}
          maxLength={OTP_MAX_LENGTH}
          required
        />

        <Button
          onClick={handleVerifyEmail}
          className="custom-button"
          disabled={loading.verifying || !isFormValid()}
          type="button"
        >
          {renderLoadingButton(loading.verifying, "Verifying...", "Verify Email")}
        </Button>
      </form>

      <p 
        onClick={handleResendVerification} 
        className="verification-resend"
        style={{ cursor: loading.resending ? 'default' : 'pointer' }}
      >
        {renderLoadingButton(loading.resending, "Sending...", "Resend OTP")}
      </p>
    </div>
  );

  const renderSuccessState = () => (
    <div className="verification-success">
      <div className="success-icon">✓</div>
      <h3>Email Verified Successfully!</h3>
      <p>You will be redirected to the dashboard shortly...</p>
      <Button onClick={redirectToDashboard} className="custom-button">
        Go to Dashboard Now
      </Button>
    </div>
  );

  const getSubtitle = () => {
    return email 
      ? `Enter the OTP sent to ${email}` 
      : 'Please verify your email address to continue';
  };

  return (
    <div className="component-body">
      <Card className="form-container email-verification text-left">
        <Card.Body className="create-form-body">
          <h4 className="subtitle mb-5">{getSubtitle()}</h4>

					{alert.show && (
            <Alert variant={alert.type} onClose={hideAlert}>
              {alert.message}
            </Alert>
          )}

          {isVerified ? renderSuccessState() : renderVerificationForm()}
        </Card.Body>
      </Card>
    </div>
  );
};

export default EmailVerification;